import { Avatar, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { EmptyState, ErrorState, HelpButton, LoadingState } from "../components";
import { StatusChip } from "../components/StatusChip";
import { formatRelative } from "../i18n/format";
import help from "./HELP.md?raw";
import { useAddComment, useTimeline, type Activity } from "./useData";

function Entry({ a }: { a: Activity }) {
  const initials = (a.actor?.name ?? "System").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 1 }}>
      <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{initials}</Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2">
          <strong>{a.actor?.name ?? "System"}</strong>{" "}
          {a.kind === "comment" ? "commented" : a.kind === "status" ? "changed status" : a.summary.toLowerCase()}
          {a.kind === "status" && a.data.to_status ? <> to <StatusChip status={String(a.data.to_status)} sx={{ ml: 0.5 }} /></> : null}
        </Typography>
        {a.kind === "comment" && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{String(a.data.text ?? a.summary)}</Typography>}
        <Typography variant="caption" color="text.secondary" title={a.created_at}>{formatRelative(a.created_at)}</Typography>
      </Box>
    </Stack>
  );
}

/** Drop-in timeline + comment box for any aggregate: <ActivityTimeline entityType="sales.sales_order" objectId={id} /> */
export function ActivityTimeline({ entityType, objectId, canComment = true }: { entityType: string; objectId: string; canComment?: boolean }) {
  const timeline = useTimeline(entityType, objectId);
  const add = useAddComment(entityType, objectId);
  const [text, setText] = useState("");
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">Activity</Typography>
        <HelpButton content={help} title="Activity timeline" size="small" />
      </Stack>
      {canComment && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField size="small" fullWidth multiline maxRows={4} placeholder="Add a comment…" value={text}
            onChange={(e) => setText(e.target.value)} inputProps={{ "aria-label": "Comment" }} />
          <Button variant="outlined" disabled={!text.trim() || add.isPending}
            onClick={() => add.mutate(text, { onSuccess: () => setText("") })}>Post</Button>
        </Stack>
      )}
      {add.isError && <ErrorState message={add.error.message} />}
      {timeline.isLoading && <LoadingState />}
      {timeline.isError && <ErrorState message={timeline.error.message} onRetry={() => void timeline.refetch()} />}
      {timeline.data && timeline.data.length === 0 && <EmptyState title="No activity yet" />}
      {timeline.data?.map((a) => <Entry key={a.id} a={a} />)}
    </Box>
  );
}
